import dayjs from 'dayjs';

const num = (v) => Number(v) || 0;

/**
 * The span between two dates as whole years, months and days. With
 * `inclusive` the last day counts as worked (an employment from 01-01 to
 * 31-12 is a whole year).
 */
export const spanBetween = (from, to, { inclusive = false } = {}) => {
  const start = dayjs(from);
  const end = inclusive ? dayjs(to).add(1, 'day') : dayjs(to);
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return null;
  const years = end.diff(start, 'year');
  const afterYears = start.add(years, 'year');
  const months = end.diff(afterYears, 'month');
  const days = end.diff(afterYears.add(months, 'month'), 'day');
  return { years, months, days };
};

const validDate = (value) => {
  if (!value) return null;
  const date = dayjs(value);
  return date.isValid() ? date.startOf('day') : null;
};

/**
 * Total work experience and the time spent in this company.
 *
 * - Time in the company runs from the employment start (`employmentSettings
 *   .startDate`, which a contract extension keeps and a rehire after a gap
 *   resets on the new profile) up to today, or up to the last day of
 *   employment once it has passed — no experience is earned after it.
 * - The total is the experience entered for before joining plus the time
 *   since the date the app counts on from. That date is optional: when it is
 *   not entered, counting starts with the employment start, so the total is
 *   at least the time in the company and previous experience is never a
 *   condition for it.
 *
 * @param {Object} [workExperience] - Entered experience before joining.
 * @param {*} [countingDate] - `workExperienceStartDate`, may be empty.
 * @param {*} [endDate] - `employmentSettings.endDate`.
 * @param {*} [employmentStart] - `employmentSettings.startDate`.
 * @returns {{ total, entered, hasEntered, since, from, fromDefault, until,
 *   isCounting, inFirm, employmentStart } | null} null when there is nothing
 *   to count: no experience entered and no date to count from.
 */
export const workExperienceTotal = (
  workExperience,
  countingDate,
  endDate,
  employmentStart
) => {
  const entered = {
    years: num(workExperience?.years),
    months: num(workExperience?.months),
    days: num(workExperience?.days),
  };
  const hasEntered = !!(entered.years || entered.months || entered.days);
  const started = validDate(employmentStart);
  const counting = validDate(countingDate);
  const from = counting || started;
  if (!hasEntered && !from) return null;

  const today = dayjs().startOf('day');
  const end = validDate(endDate);
  const ended = !!end && end.isBefore(today);
  const until = ended ? end : today;
  // The last day of employment was worked; today is still under way.
  const span = (start) => spanBetween(start, until, { inclusive: ended });
  const inFirm = started ? span(started) : null;

  if (!from) {
    return {
      total: entered,
      entered,
      hasEntered,
      since: null,
      from: null,
      fromDefault: false,
      until: null,
      isCounting: !ended,
      inFirm,
      employmentStart: started,
    };
  }

  // Count from a start moved back by the entered experience, so months and
  // days carry over as calendar months, not as 30-day blocks.
  const anchor = from
    .subtract(entered.years, 'year')
    .subtract(entered.months, 'month')
    .subtract(entered.days, 'day');

  return {
    // A start still ahead has earned nothing yet.
    total: from.isAfter(until) ? entered : (span(anchor) ?? entered),
    entered,
    hasEntered,
    since: span(from),
    from,
    fromDefault: !counting,
    until,
    isCounting: !ended,
    inFirm,
    employmentStart: started,
  };
};

/** "12 god 4 mes 3 dana", leaving out the zero parts. */
export const formatSpan = (span, t) => {
  if (!span) return '';
  const days = (count) =>
    t('employeeProfile.experienceUi.days', { ns: 'translations', count });
  const parts = [
    span.years && `${span.years} ${t('formValidation.years', { ns: 'forms' })}`,
    span.months &&
      `${span.months} ${t('formValidation.months', { ns: 'forms' })}`,
    span.days && days(span.days),
  ].filter(Boolean);
  return parts.length ? parts.join(' ') : days(0);
};
